import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getProducts, getCustomers, createInvoice, downloadInvoicePDF, getBranchInventory, getBranches, getTailoringOrder, downloadTailoringReceipt, sendTailoringReceipt, updateCustomer, sendInvoiceEmail } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { ShoppingCart, Trash2, Plus, Minus, Receipt, Search, X, Building2 } from 'lucide-react';

const POS = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const tailoringOrderId = new URLSearchParams(location.search).get('tailoring_order_id') || '';
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(user?.branch_id || '');
  const [branchInventory, setBranchInventory] = useState([]); // Inventario por sucursal
  const [cart, setCart] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [invoiceType, setInvoiceType] = useState('consumo');
  const [paymentMethod, setPaymentMethod] = useState('efectivo');
  const [includeItbis, setIncludeItbis] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [receiptModal, setReceiptModal] = useState(null);
  const [receiptTimer, setReceiptTimer] = useState(0);
  const [receiptTimerActive, setReceiptTimerActive] = useState(false);
  const [sendingReceipt, setSendingReceipt] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [askUpdateEmail, setAskUpdateEmail] = useState(null);
  const [showCart, setShowCart] = useState(false); // Para mobile
  const [tailoringOrder, setTailoringOrder] = useState(null);
  const [prefilledTailoringOrderId, setPrefilledTailoringOrderId] = useState('');
  const [tailoringPaymentAmount, setTailoringPaymentAmount] = useState('');

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedBranch) {
      loadBranchInventory(selectedBranch);
    }
  }, [selectedBranch]);

  // Receipt timer countdown
  useEffect(() => {
    if (!receiptTimerActive || receiptTimer <= 0) return;
    let fired = false; // guard against multiple fires
    const interval = setInterval(() => {
      setReceiptTimer((t) => {
        if (t <= 1) {
          clearInterval(interval);
          setReceiptTimerActive(false);
          if (!fired) {
            fired = true;
            // Use setTimeout to let state settle before auto-action
            setTimeout(() => handleReceiptAutoAction(), 100);
          }
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => { clearInterval(interval); };
  }, [receiptTimerActive]);

  useEffect(() => {
    if (tailoringOrderId && products.length > 0 && customers.length > 0 && prefilledTailoringOrderId !== tailoringOrderId) {
      loadTailoringOrderForPOS(tailoringOrderId);
    }
  
  }, [tailoringOrderId, products.length, customers.length, prefilledTailoringOrderId]);

  const loadInitialData = async () => {
    try {
      const [productsData, customersData, branchesData] = await Promise.all([
        getProducts(),
        getCustomers(),
        user?.role === 'admin' ? getBranches() : Promise.resolve([])
      ]);
      setProducts(productsData);
      setCustomers(customersData);
      setBranches(branchesData);
      
      // Si el usuario tiene sucursal asignada, cargar su inventario
      const branchId = user?.branch_id || (branchesData.length > 0 ? branchesData[0].id : '');
      if (branchId) {
        setSelectedBranch(branchId);
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    }
  };

  const loadBranchInventory = async (branchId) => {
    try {
      const inventoryData = await getBranchInventory(branchId);
      setBranchInventory(inventoryData);
      // Limpiar carrito al cambiar de sucursal, excepto cuando viene cargado desde pedido
      if (!tailoringOrderId) {
        setCart([]);
      }
    } catch (error) {
      toast.error('Error al cargar inventario');
    }
  };


  const toNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const buildTailoringCartItems = (order, amountToInvoice = null, useItbis = includeItbis) => {
    const totalAmount = toNumber(order?.pricing?.total_amount);
    const paidAmount = toNumber(order?.pricing?.paid_amount);
    const balance = Math.max(0, totalAmount - paidAmount);
    const invoiceAmount = amountToInvoice === null ? balance : toNumber(amountToInvoice);

    // Para pedidos, el monto escrito es la base del pedido (sin ITBIS).
    // Si ITBIS está activo, se suma aparte en calculateTotals().
    const targetSubtotal = invoiceAmount;

    const rawItems = [];
    const basePrice = toNumber(order?.product_ref?.price);
    rawItems.push({
      product_id: order?.product_ref?.product_id || `tailoring-${order?.id}`,
      product_name: `Confección ${order?.code || order?.id || ''} - ${order?.product_ref?.name || 'Producto base'}`,
      quantity: 1,
      raw_subtotal: basePrice > 0 ? basePrice : invoiceAmount,
    });

    (order?.services || []).forEach((service) => {
      const qty = toNumber(service.quantity) || 1;
      rawItems.push({
        product_id: service.product_id,
        product_name: service.name,
        quantity: qty,
        raw_subtotal: toNumber(service.subtotal),
      });
    });

    const rawTotal = rawItems.reduce((sum, item) => sum + toNumber(item.raw_subtotal), 0);
    const scale = rawTotal > 0 ? targetSubtotal / rawTotal : 1;

    return rawItems.map((item) => {
      const qty = item.quantity || 1;
      const subtotal = Math.max(0, toNumber(item.raw_subtotal) * scale);
      const unitPrice = subtotal / qty;
      return {
        product_id: item.product_id,
        product_name: item.product_name,
        product_type: 'SERVICIO',
        track_inventory: false,
        quantity: qty,
        unit_price: unitPrice,
        size: '',
        color: '',
        discount: 0,
        subtotal,
      };
    });
  };

  const downloadAndPrint = async (orderId, invoiceId) => {
    try {
      const blob = orderId ? await downloadTailoringReceipt(orderId) : await downloadInvoicePDF(invoiceId);
      if (!blob || blob.type === 'application/json') return;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.target = '_blank'; link.rel = 'noopener noreferrer';
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 3000);
    } catch (e) { console.error('Print error:', e); }
  };

  const pendingNavigateRef = React.useRef(null);

  const closeReceiptModal = () => {
    setReceiptModal(null);
    setReceiptTimerActive(false);
    setReceiptTimer(0);
    setEmailInput('');
    setShowEmailInput(false);
    setAskUpdateEmail(null);
    // Navigate after modal closes if there's a pending route
    if (pendingNavigateRef.current) {
      const path = pendingNavigateRef.current;
      pendingNavigateRef.current = null;
      navigate(path);
    }
  };

  const sendingRef = React.useRef(false);

  const doSendEmail = async (email, orderId, invoiceId) => {
    if (sendingRef.current) return; // prevent double-send
    sendingRef.current = true;
    setSendingReceipt(true);
    try {
      if (orderId) {
        await sendTailoringReceipt(orderId, email);
      } else if (invoiceId) {
        await sendInvoiceEmail(invoiceId, email);
      } else {
        throw new Error('No hay referencia de factura para enviar');
      }
      toast.success(`Comprobante enviado a ${email}`);
    } catch (e) {
      console.error('Email error:', e);
      toast.error(e.response?.data?.error || e.message || 'Error enviando comprobante');
    } finally {
      sendingRef.current = false;
      setSendingReceipt(false);
      closeReceiptModal();
    }
  };

  const handleReceiptAutoAction = () => {
    setReceiptModal((current) => {
      if (!current) return current;
      if (current.customerEmail) {
        doSendEmail(current.customerEmail, current.orderId, current.invoice?.id);
      } else {
        downloadAndPrint(current.orderId, current.invoice?.id);
        closeReceiptModal();
      }
      return current;
    });
  };

  const handleReceiptModalEmail = async () => {
    const email = emailInput.trim();
    if (!email || !email.includes('@')) { toast.error('Correo inválido'); return; }
    setReceiptTimerActive(false);
    if (receiptModal?.customerEmail && email !== receiptModal.customerEmail && receiptModal?.customerId) {
      setAskUpdateEmail(email);
      return;
    }
    await doSendEmail(email, receiptModal?.orderId, receiptModal?.invoice?.id);
  };

  const confirmUpdateEmail = async (doUpdate) => {
    const email = askUpdateEmail;
    if (doUpdate && receiptModal?.customerId) {
      try {
        await updateCustomer(receiptModal.customerId, { email });
        toast.success('Correo del cliente actualizado');
      } catch (e) { console.error(e); }
    }
    setAskUpdateEmail(null);
    await doSendEmail(email, receiptModal?.orderId, receiptModal?.invoice?.id);
  };

  const openReceiptModal = (invoice, orderId, customer) => {
    const pref  = user?.receipt_preference || 'ask';
    const timer = user?.receipt_timer || 15;
    const hasEmail = !!customer?.email;

    if (pref === 'print') {
      downloadAndPrint(orderId, invoice?.id);
      return;
    }
    if (pref === 'email' && hasEmail) {
      setReceiptModal({ invoice, orderId, customerEmail: customer.email, customerName: `${customer.name} ${customer.last_name}`, customerId: customer.id });
      setReceiptTimer(timer);
      setReceiptTimerActive(true);
      return;
    }
    if (pref === 'email' && !hasEmail) {
      setReceiptModal({ invoice, orderId, customerEmail: '', customerName: customer ? `${customer.name} ${customer.last_name}` : '', customerId: customer?.id });
      setShowEmailInput(true);
      return;
    }
    // ask
    setReceiptModal({ invoice, orderId, customerEmail: customer?.email || '', customerName: customer ? `${customer.name} ${customer.last_name}` : 'Cliente', customerId: customer?.id });
    if (hasEmail) { setReceiptTimer(timer); setReceiptTimerActive(true); }
  };

  const loadTailoringOrderForPOS = async (orderId) => {
    try {
      const order = await getTailoringOrder(orderId);
      const totalAmount = toNumber(order?.pricing?.total_amount);
      const paidAmount = toNumber(order?.pricing?.paid_amount);
      const balance = Math.max(0, totalAmount - paidAmount);

      if (order?.status === 'CANCELADO') {
        toast.error('No se puede facturar un pedido cancelado');
        return;
      }
      if (balance <= 0) {
        toast.error('Este pedido ya está saldado');
        navigate(`/tailoring-orders/${orderId}`);
        return;
      }

      setTailoringOrder(order);
      setPrefilledTailoringOrderId(orderId);
      if (order.branch_id) {
        setSelectedBranch(order.branch_id);
      }

      const customer = customers.find((c) => c.id === order.customer_id);
      if (customer) {
        setSelectedCustomer(customer);
      } else {
        setSelectedCustomer({ id: order.customer_id, name: order.customer_name || 'Cliente', last_name: '' });
      }

      setIncludeItbis(false);
      setTailoringPaymentAmount(String(balance.toFixed(2)));
      setCart(buildTailoringCartItems(order, balance, false));
      setDiscount(0);
      setSearchTerm('');
      toast.success(`Pedido ${order.code || order.id} cargado en POS`);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Error cargando pedido en POS');
    }
  };

  // Obtener stock de un producto/variante desde el inventario de la sucursal
  const getVariantStock = (productId, size, color) => {
    const inv = branchInventory.find(
      i => i.product_id === productId && 
           (i.size || '') === (size || '') && 
           (i.color || '') === (color || '')
    );
    return inv?.stock || 0;
  };

  const isService = (product) => {
    const productType = String(
      product?.product_type ||
      product?.productType ||
      product?.type ||
      ''
    ).trim().toUpperCase();

    const trackInventory = product?.track_inventory;

    return (
      productType === 'SERVICIO' ||
      productType === 'SERVICE' ||
      trackInventory === false ||
      trackInventory === 'false' ||
      trackInventory === 0
    );
  };

  // Obtener todas las variantes con stock para un producto
  const getProductVariantsWithStock = (product) => {
    if (isService(product)) {
      return [{ size: '', color: '', stock: null, isService: true }];
    }

    // Obtener variantes del inventario de sucursal para este producto
    const productInventory = branchInventory.filter(i => i.product_id === product.id);
    
    if (productInventory.length > 0) {
      return productInventory.map(inv => ({
        size: inv.size || '',
        color: inv.color || '',
        stock: inv.stock || 0
      }));
    }
    
    // Fallback: Si no hay inventario de sucursal, usar las combinaciones definidas en el producto
    // pero con stock 0 (para indicar que no hay stock en esta sucursal)
    const variants = [];
    const sizes = product.sizes?.length > 0 ? product.sizes : [''];
    const colors = product.colors?.length > 0 ? product.colors : [''];
    
    for (const size of sizes) {
      for (const color of colors) {
        variants.push({ size, color, stock: 0 });
      }
    }
    
    return variants;
  };

  const addToCart = (product, size, color, availableStock) => {
    const service = isService(product);

    if (!service && availableStock <= 0) {
      toast.error('Producto sin stock en esta sucursal');
      return;
    }

    const existingItem = cart.find(
      item => item.product_id === product.id && item.size === size && item.color === color
    );

    if (existingItem) {
      if (!service && existingItem.quantity >= availableStock) {
        toast.error('Stock insuficiente');
        return;
      }
      updateQuantity(existingItem, existingItem.quantity + 1);
    } else {
      const newItem = {
        product_id: product.id,
        product_name: product.name,
        product_type: service ? 'SERVICIO' : (product.product_type || 'PRODUCTO'),
        track_inventory: !service,
        quantity: 1,
        unit_price: product.price,
        size: size,
        color: color,
        discount: 0,
        subtotal: product.price
      };
      setCart([...cart, newItem]);
      toast.success(`${product.name} agregado al carrito`);
    }
  };

  const updateQuantity = (item, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(item);
      return;
    }

    // Validar que no exceda el stock disponible solo en productos físicos
    if (item.track_inventory !== false) {
      const availableStock = getVariantStock(item.product_id, item.size, item.color);
      if (newQuantity > availableStock) {
        toast.error('Stock insuficiente');
        return;
      }
    }

    const updatedCart = cart.map(cartItem => {
      if (cartItem === item) {
        return {
          ...cartItem,
          quantity: newQuantity,
          subtotal: cartItem.unit_price * newQuantity
        };
      }
      return cartItem;
    });
    setCart(updatedCart);
  };

  const removeFromCart = (item) => {
    setCart(cart.filter(cartItem => cartItem !== item));
  };

  const calculateTotals = () => {
    const subtotal = cart.reduce((sum, item) => sum + item.subtotal, 0);
    const itbis = includeItbis ? subtotal * 0.18 : 0;
    const total = subtotal + itbis - discount;
    return { subtotal, itbis, total };
  };

  const handleTailoringPaymentAmountChange = (value) => {
    setTailoringPaymentAmount(value);

    if (!tailoringOrder) return;

    const totalAmount = toNumber(tailoringOrder?.pricing?.total_amount);
    const paidAmount = toNumber(tailoringOrder?.pricing?.paid_amount);
    const balance = Math.max(0, totalAmount - paidAmount);
    let amount = toNumber(value);

    if (amount < 0) amount = 0;
    if (amount > balance) amount = balance;

    setCart(buildTailoringCartItems(tailoringOrder, amount, includeItbis));
  };

  const toggleItbis = () => {
    const next = !includeItbis;
    setIncludeItbis(next);

    if (tailoringOrder) {
      const totalAmount = toNumber(tailoringOrder?.pricing?.total_amount);
      const paidAmount = toNumber(tailoringOrder?.pricing?.paid_amount);
      const balance = Math.max(0, totalAmount - paidAmount);
      let amount = toNumber(tailoringPaymentAmount);
      if (amount <= 0 || amount > balance) amount = balance;
      setCart(buildTailoringCartItems(tailoringOrder, amount, next));
    }
  };

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error('El carrito está vacío');
      return;
    }

    // Validate credit payment requires a customer
    if (paymentMethod === 'credito' && !selectedCustomer) {
      toast.error('Debe seleccionar un cliente para ventas a crédito');
      return;
    }

    // Validar que haya sucursal seleccionada
    if (!selectedBranch) {
      toast.error('Debe seleccionar una sucursal');
      return;
    }

    setLoading(true);
    try {
      let itemsForInvoice = cart;

      if (tailoringOrder) {
        const totalAmount = toNumber(tailoringOrder?.pricing?.total_amount);
        const paidAmount = toNumber(tailoringOrder?.pricing?.paid_amount);
        const balance = Math.max(0, totalAmount - paidAmount);
        const amount = toNumber(tailoringPaymentAmount);

        if (amount <= 0) {
          toast.error('El monto del abono debe ser mayor que cero');
          setLoading(false);
          return;
        }

        if (amount > balance) {
          toast.error('El abono no puede ser mayor al balance pendiente');
          setLoading(false);
          return;
        }

        itemsForInvoice = buildTailoringCartItems(tailoringOrder, amount, includeItbis);
      }

      const invoiceData = {
        customer_id: selectedCustomer?.id || null,
        customer_name: selectedCustomer ? `${selectedCustomer.name} ${selectedCustomer.last_name}` : 'Consumidor Final',
        invoice_type: invoiceType,
        items: itemsForInvoice,
        discount: discount,
        payment_method: paymentMethod,
        include_itbis: includeItbis,
        branch_id: selectedBranch,  // Enviar la sucursal seleccionada
        tailoring_order_id: tailoringOrder?.id || null,
        notes: tailoringOrder ? `Factura generada desde pedido de confección ${tailoringOrder.code || tailoringOrder.id}` : ''
      };

      const invoice = await createInvoice(invoiceData, user.id);
      toast.success(`Factura ${invoice.invoice_number} creada exitosamente`);

      const currentCustomer = selectedCustomer;
      const currentOrderId  = tailoringOrder?.id || null;

      // Clear POS immediately
      setSelectedCustomer(null);
      setCart([]);
      setDiscount(0);
      setTailoringPaymentAmount('');
      setIncludeItbis(false);
      loadBranchInventory(selectedBranch);

      // Open modal with saved customer data
      // For tailoring orders, navigate AFTER modal closes (not immediately)
      if (currentOrderId) {
        pendingNavigateRef.current = `/tailoring-orders/${currentOrderId}`;
      }
      openReceiptModal(invoice, currentOrderId, currentCustomer);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.response?.data?.error || 'Error al crear factura');
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const { subtotal, itbis, total } = calculateTotals();

  // Mostrar advertencia si no hay sucursal seleccionada y no es admin
  if (!selectedBranch && user?.role !== 'admin') {
    return (
      <div className="p-6 md:p-8" data-testid="pos-page">
        <h1 className="text-4xl font-bold mb-6" style={{ fontFamily: 'Outfit' }}>Punto de Venta</h1>
        <div className="bg-amber-50 border border-amber-200 p-6 rounded-lg">
          <div className="flex items-center gap-3 text-amber-800">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="font-semibold">No tiene una sucursal asignada</p>
              <p className="text-sm">Contacte al administrador para que le asigne una sucursal y poder realizar ventas.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Obtener nombre de la sucursal seleccionada
  const currentBranchName = branches.find(b => b.id === selectedBranch)?.name || 'Sucursal';

  return (
    <div className="h-screen flex flex-col overflow-hidden" data-testid="pos-page">
      {/* Header con botón de carrito para mobile */}
      <div className="p-4 bg-white border-b border-slate-200 lg:hidden flex items-center justify-between">
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>POS</h1>
        <Button
          onClick={() => setShowCart(true)}
          className="bg-cyan-500 hover:bg-cyan-600 relative"
          data-testid="mobile-cart-button"
        >
          <ShoppingCart className="w-5 h-5" />
          {cart.length > 0 && (
            <span className="absolute -top-2 -right-2 bg-rose-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold">
              {cart.length}
            </span>
          )}
        </Button>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Products Section */}
        <div className="flex-1 overflow-y-auto p-4 lg:p-6 bg-slate-50">
          <div className="mb-4 lg:mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
              <h1 className="hidden lg:block text-4xl font-bold" style={{ fontFamily: 'Outfit' }}>Punto de Venta</h1>
              
              {/* Selector de sucursal para admin */}
              {user?.role === 'admin' && branches.length > 0 && (
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-slate-500" />
                  <Select value={selectedBranch} onValueChange={setSelectedBranch} disabled={!!tailoringOrder}>
                    <SelectTrigger className="w-48 bg-white" data-testid="pos-branch-select">
                      <SelectValue placeholder="Seleccionar sucursal" />
                    </SelectTrigger>
                    <SelectContent>
                      {branches.map((branch) => (
                        <SelectItem key={branch.id} value={branch.id}>
                          {branch.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            
            {/* Info de sucursal actual */}
            {selectedBranch && (
              <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-2 mb-4 flex items-center gap-2 text-cyan-800 text-sm">
                <Building2 className="w-4 h-4" />
                <span>Vendiendo en: <strong>{currentBranchName}</strong></span>
              </div>
            )}

            {tailoringOrder && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-amber-900 text-sm flex items-center justify-between gap-3">
                <div>
                  <div className="font-semibold">Facturando pedido de confección {tailoringOrder.code || tailoringOrder.id}</div>
                  <div>Cliente: {tailoringOrder.customer_name} · Balance pendiente: <strong>${Math.max(0, Number(tailoringOrder?.pricing?.total_amount || 0) - Number(tailoringOrder?.pricing?.paid_amount || 0)).toFixed(2)}</strong></div>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate(`/tailoring-orders/${tailoringOrder.id}`)}>
                  Volver al pedido
                </Button>
              </div>
            )}
            
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Buscar productos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white border-slate-200 h-11 lg:h-12"
                data-testid="pos-search-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 lg:gap-4" data-testid="pos-products-grid">
          {filteredProducts.map(product => {
            const variants = getProductVariantsWithStock(product);
            const service = isService(product);
            const hasAnyStock = service || variants.some(v => v.stock > 0);
            
            return (
              <Card
                key={product.id}
                className={`cursor-pointer hover:shadow-lg transition-shadow duration-200 border border-slate-200 ${!hasAnyStock ? 'opacity-60' : ''}`}
                data-testid={`product-card-${product.id}`}
              >
                <CardHeader className="p-3 lg:p-4">
                  <CardTitle className="text-base lg:text-lg" style={{ fontFamily: 'Outfit' }}>{product.name}</CardTitle>
                  <p className="text-xs lg:text-sm text-slate-500">{product.category}{service ? ' · Servicio' : ''}</p>
                  <p className="text-xl lg:text-2xl font-bold text-cyan-600" style={{ fontFamily: 'JetBrains Mono' }}>
                    ${product.price.toFixed(2)}
                  </p>
                </CardHeader>
                <CardContent className="p-3 lg:p-4 pt-0 space-y-2">
                  {variants.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-2">Sin variantes definidas</p>
                  ) : (
                    variants.map((variant, idx) => {
                      const label = [variant.size, variant.color].filter(Boolean).join(' - ') || 'Único';
                      return (
                        <Button
                          key={idx}
                          onClick={() => addToCart(product, variant.size, variant.color, variant.stock)}
                          disabled={!service && variant.stock <= 0}
                          className="w-full text-xs lg:text-sm bg-slate-900 hover:bg-slate-800 h-8 lg:h-9 disabled:opacity-50"
                          size="sm"
                          data-testid={`add-to-cart-${product.id}-${idx}`}
                        >
                          {service ? 'Agregar servicio' : `${label} (${variant.stock})`}
                        </Button>
                      );
                    })
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Cart Section - Desktop (sidebar) */}
      <div className="hidden lg:flex w-[400px] bg-white border-l border-slate-200 flex-col" data-testid="pos-cart-desktop">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-2xl font-bold flex items-center" style={{ fontFamily: 'Outfit' }}>
            <ShoppingCart className="w-6 h-6 mr-2 text-cyan-500" />
            Carrito ({cart.length})
          </h2>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          {cart.map((item, index) => (
            <div key={index} className="bg-slate-50 p-3 rounded-lg border border-slate-200" data-testid={`cart-item-${index}`}>
              <div className="flex justify-between items-start mb-2">
                <div>
                  <p className="font-medium">{item.product_name}</p>
                  <p className="text-sm text-slate-500">{item.size} - {item.color}</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeFromCart(item)}
                  className="text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                  data-testid={`remove-item-${index}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => updateQuantity(item, item.quantity - 1)}
                    className="h-8 w-8"
                    data-testid={`decrease-qty-${index}`}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="w-12 text-center font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                    {item.quantity}
                  </span>
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => updateQuantity(item, item.quantity + 1)}
                    className="h-8 w-8"
                    data-testid={`increase-qty-${index}`}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <p className="font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                  ${item.subtotal.toFixed(2)}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="p-6 border-t border-slate-200 space-y-4">
          <div className="space-y-2">
            <Label>Cliente</Label>
            <Select
              value={selectedCustomer?.id || 'none'}
              onValueChange={(value) => {
                const customer = customers.find(c => c.id === value);
                setSelectedCustomer(customer || null);
              }}
            >
              <SelectTrigger data-testid="pos-customer-select">
                <SelectValue placeholder="Consumidor Final" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Consumidor Final</SelectItem>
                {customers.map(customer => (
                  <SelectItem key={customer.id} value={customer.id}>
                    {customer.name} {customer.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={invoiceType} onValueChange={setInvoiceType}>
                <SelectTrigger data-testid="pos-invoice-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="consumo">Consumo</SelectItem>
                  <SelectItem value="credito_fiscal">Crédito Fiscal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Método de Pago</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger data-testid="pos-payment-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="efectivo">Efectivo</SelectItem>
                  <SelectItem value="tarjeta">Tarjeta</SelectItem>
                  <SelectItem value="transferencia">Transferencia</SelectItem>
                  <SelectItem value="credito">Crédito</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>


          <div className="space-y-2">
            <Label>ITBIS</Label>
            <Button
              type="button"
              variant={includeItbis ? "default" : "outline"}
              onClick={toggleItbis}
              className={includeItbis ? "w-full bg-emerald-600 hover:bg-emerald-700 text-white" : "w-full"}
            >
              {includeItbis ? "ITBIS activado" : "ITBIS desactivado"}
            </Button>
          </div>

          <div className="space-y-2">
            <Label>Descuento</Label>
            <Input
              type="number"
              value={discount}
              onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
              placeholder="0.00"
              className="mono-font"
              data-testid="pos-discount-input"
            />
          </div>

          {tailoringOrder && (
            <div className="p-3 rounded-lg border border-amber-200 bg-amber-50 space-y-2">
              <div className="text-sm font-medium text-amber-900">
                Pedido {tailoringOrder.code || tailoringOrder.id}
              </div>
              <div className="text-xs text-amber-800">
                Puedes cobrar un abono o el balance completo. El monto se toma sin ITBIS.
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">Monto base a cobrar / abono (sin ITBIS)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={tailoringPaymentAmount}
                  onChange={(e) => handleTailoringPaymentAmountChange(e.target.value)}
                  className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
            </div>
          )}

          <div className="space-y-2 pt-4 border-t border-slate-200">
            <div className="flex justify-between text-sm">
              <span>Subtotal:</span>
              <span className="mono-font font-medium">${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>ITBIS {includeItbis ? '(18%)' : '(no aplicado)'}:</span>
              <span className="mono-font font-medium">${itbis.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Descuento:</span>
              <span className="mono-font font-medium text-rose-500">-${discount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-xl font-bold pt-2 border-t border-slate-200">
              <span>TOTAL:</span>
              <span className="mono-font text-cyan-600">${total.toFixed(2)}</span>
            </div>
          </div>

          <Button
            onClick={handleCheckout}
            disabled={loading || cart.length === 0}
            className="w-full bg-cyan-500 hover:bg-cyan-600 text-white h-12 text-lg font-medium shadow-md shadow-cyan-500/20"
            data-testid="pos-checkout-button"
          >
            {loading ? 'Procesando...' : (
              <>
                <Receipt className="w-5 h-5 mr-2" />
                {tailoringOrder ? 'Facturar / Registrar abono' : 'Generar Factura'}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Mobile Cart Overlay */}
      {showCart && (
        <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-50" onClick={() => setShowCart(false)}>
          <div className="absolute right-0 top-0 h-full w-full max-w-sm bg-white flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-xl font-bold flex items-center" style={{ fontFamily: 'Outfit' }}>
                <ShoppingCart className="w-5 h-5 mr-2 text-cyan-500" />
                Carrito ({cart.length})
              </h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowCart(false)}
                className="text-slate-500 hover:text-slate-700"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {cart.map((item, index) => (
                <div key={index} className="bg-slate-50 p-3 rounded-lg border border-slate-200" data-testid={`mobile-cart-item-${index}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-medium text-sm">{item.product_name}</p>
                      <p className="text-xs text-slate-500">{item.size} - {item.color}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeFromCart(item)}
                      className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 h-8 w-8"
                      data-testid={`mobile-remove-item-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => updateQuantity(item, item.quantity - 1)}
                        className="h-7 w-7"
                        data-testid={`mobile-decrease-qty-${index}`}
                      >
                        <Minus className="w-3 h-3" />
                      </Button>
                      <span className="w-8 text-center font-bold text-sm" style={{ fontFamily: 'JetBrains Mono' }}>
                        {item.quantity}
                      </span>
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => updateQuantity(item, item.quantity + 1)}
                        className="h-7 w-7"
                        data-testid={`mobile-increase-qty-${index}`}
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                    <p className="font-bold text-sm" style={{ fontFamily: 'JetBrains Mono' }}>
                      ${item.subtotal.toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 border-t border-slate-200 space-y-3">
              <div className="space-y-2">
                <Label className="text-sm">Cliente</Label>
                <Select
                  value={selectedCustomer?.id || 'none'}
                  onValueChange={(value) => {
                    const customer = customers.find(c => c.id === value);
                    setSelectedCustomer(customer || null);
                  }}
                >
                  <SelectTrigger className="h-9" data-testid="mobile-customer-select">
                    <SelectValue placeholder="Consumidor Final" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Consumidor Final</SelectItem>
                    {customers.map(customer => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.name} {customer.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <Label className="text-sm">Tipo</Label>
                  <Select value={invoiceType} onValueChange={setInvoiceType}>
                    <SelectTrigger className="h-9" data-testid="mobile-invoice-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="consumo">Consumo</SelectItem>
                      <SelectItem value="credito_fiscal">Crédito Fiscal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Método de Pago</Label>
                  <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                    <SelectTrigger className="h-9" data-testid="mobile-payment-method-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="efectivo">Efectivo</SelectItem>
                      <SelectItem value="tarjeta">Tarjeta</SelectItem>
                      <SelectItem value="transferencia">Transferencia</SelectItem>
                      <SelectItem value="credito">Crédito</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>


              <div className="space-y-2">
                <Label className="text-sm">ITBIS</Label>
                <Button
                  type="button"
                  variant={includeItbis ? "default" : "outline"}
                  onClick={toggleItbis}
                  className={includeItbis ? "w-full h-9 bg-emerald-600 hover:bg-emerald-700 text-white" : "w-full h-9"}
                >
                  {includeItbis ? "ITBIS activado" : "ITBIS desactivado"}
                </Button>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Descuento</Label>
                <Input
                  type="number"
                  value={discount}
                  onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                  placeholder="0.00"
                  className="mono-font h-9"
                  data-testid="mobile-discount-input"
                />
              </div>

              <div className="space-y-2 pt-3 border-t border-slate-200">
                <div className="flex justify-between text-sm">
                  <span>Subtotal:</span>
                  <span className="mono-font font-medium">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>ITBIS {includeItbis ? '(18%)' : '(no aplicado)'}:</span>
                  <span className="mono-font font-medium">${itbis.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Descuento:</span>
                  <span className="mono-font font-medium text-rose-500">-${discount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-slate-200">
                  <span>TOTAL:</span>
                  <span className="mono-font text-cyan-600">${total.toFixed(2)}</span>
                </div>
              </div>

              <Button
                onClick={handleCheckout}
                disabled={loading || cart.length === 0}
                className="w-full bg-cyan-500 hover:bg-cyan-600 text-white h-10 text-base font-medium shadow-md shadow-cyan-500/20"
                data-testid="mobile-checkout-button"
              >
                {loading ? 'Procesando...' : (
                  <>
                    <Receipt className="w-4 h-4 mr-2" />
                    {tailoringOrder ? 'Facturar / Registrar abono' : 'Generar Factura'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}


      {/* ── Receipt Modal ── */}
      {receiptModal && !askUpdateEmail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="bg-cyan-500 px-6 py-4 text-white">
              <h2 className="font-bold text-lg">Comprobante listo</h2>
              <p className="text-cyan-100 text-sm">{receiptModal.customerName || 'Cliente'}</p>
            </div>
            <div className="p-6 space-y-4">
              {receiptModal.customerEmail && receiptTimerActive && !showEmailInput && (
                <div className="space-y-2">
                  <p className="text-sm text-slate-600 text-center">
                    Enviando a <strong>{receiptModal.customerEmail}</strong> en {receiptTimer}s...
                  </p>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div
                      className="bg-cyan-500 h-2 rounded-full transition-all duration-1000"
                      style={{ width: `${(receiptTimer / (user?.receipt_timer || 15)) * 100}%` }}
                    />
                  </div>
                </div>
              )}
              {showEmailInput && (
                <div className="space-y-2">
                  <Label className="text-sm">Correo del cliente</Label>
                  <Input
                    type="email"
                    placeholder="cliente@ejemplo.com"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleReceiptModalEmail()}
                    autoFocus
                  />
                </div>
              )}
              <div className="grid grid-cols-1 gap-2">
                {receiptModal.customerEmail && !showEmailInput && (
                  <button
                    onClick={() => { setReceiptTimerActive(false); doSendEmail(receiptModal.customerEmail, receiptModal.orderId, receiptModal.invoice?.id); }}
                    disabled={sendingReceipt}
                    className="flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white font-medium py-3 px-4 rounded-xl transition-colors"
                  >
                    📧 {sendingReceipt ? 'Enviando...' : `Enviar a ${receiptModal.customerEmail}`}
                  </button>
                )}
                {!showEmailInput && (
                  <button
                    onClick={() => { setReceiptTimerActive(false); setShowEmailInput(true); }}
                    className="flex items-center justify-center gap-2 border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium py-3 px-4 rounded-xl transition-colors"
                  >
                    📧 {receiptModal.customerEmail ? 'Enviar a otro correo' : 'Enviar por correo'}
                  </button>
                )}
                {showEmailInput && (
                  <button
                    onClick={handleReceiptModalEmail}
                    disabled={sendingReceipt}
                    className="flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white font-medium py-3 px-4 rounded-xl transition-colors"
                  >
                    📧 {sendingReceipt ? 'Enviando...' : 'Enviar'}
                  </button>
                )}
                <button
                  onClick={() => { setReceiptTimerActive(false); downloadAndPrint(receiptModal.orderId, receiptModal.invoice?.id); closeReceiptModal(); }}
                  className="flex items-center justify-center gap-2 border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium py-3 px-4 rounded-xl transition-colors"
                >
                  🖨️ Imprimir comprobante
                </button>
                <button
                  onClick={() => { setReceiptTimerActive(false); closeReceiptModal(); }}
                  className="text-slate-400 hover:text-slate-600 text-sm py-2 transition-colors"
                >
                  Omitir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Ask to update email ── */}
      {askUpdateEmail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 space-y-4">
            <h2 className="font-bold text-lg">¿Actualizar correo del cliente?</h2>
            <p className="text-sm text-slate-600">
              ¿Deseas guardar <strong>{askUpdateEmail}</strong> como correo de <strong>{receiptModal?.customerName}</strong>?
            </p>
            <div className="flex gap-2">
              <button onClick={() => confirmUpdateEmail(false)} className="flex-1 border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium py-2 px-4 rounded-xl">
                No, solo enviar
              </button>
              <button onClick={() => confirmUpdateEmail(true)} className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white font-medium py-2 px-4 rounded-xl">
                Sí, actualizar
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default POS;